import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { logger } from '@onyx/logger';

import type { SnackbarProps } from './snack-bar';
import { Snackbar } from './snack-bar';
import styles from './snack-bar-provider.module.css';

const defaultSnackbarTimeout = 10000;
const minSnackbarTimeout = 1000;
const maxSnackbarTimeout = 60000;

type SnackbarContextType = {
  showSnackbar: (props: SnackbarProps & { timeout?: number }) => void;
  hideSnackbar: () => void;
};

const SnackbarContext = createContext<SnackbarContextType | null>(null);

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbar, setSnackbar] = useState<
    (SnackbarProps & { timeout?: number }) | null
  >(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  const showSnackbar = useCallback(
    (props: SnackbarProps & { timeout?: number }) => {
      setSnackbar(null);
      const timeout = props.timeout ?? defaultSnackbarTimeout;
      const clampedTimeout = Math.min(
        Math.max(timeout, minSnackbarTimeout),
        maxSnackbarTimeout
      );
      setTimeout(() => {
        setSnackbar({
          ...props,
          timeout: clampedTimeout,
        });
      }, 50);
    },
    []
  );

  const hideSnackbar = useCallback(() => {
    setSnackbar(null);
  }, []);

  useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  useEffect(() => {
    if (!snackbar || !snackbar.timeout) {
      return;
    }

    const timer = setTimeout(() => setSnackbar(null), snackbar.timeout);
    return () => clearTimeout(timer);
  }, [snackbar]);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      {portalContainer &&
        createPortal(
          <div
            className={styles.snackbarContainer}
            role="status"
            aria-live="polite"
          >
            {snackbar && (
              <Snackbar {...snackbar} onClose={() => setSnackbar(null)} />
            )}
          </div>,
          portalContainer
        )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const ctx = useContext(SnackbarContext);

  if (!ctx) {
    logger.warn({
      event: 'snackbar_used_outside_provider',
      message: 'useSnackbar must be used within a SnackbarProvider',
    });
    return {
      showSnackbar: () => {
        logger.warn({
          event: 'snackbar_show_outside_provider',
          message: 'showSnackbar called outside SnackbarProvider',
        });
      },
      hideSnackbar: () => {
        logger.warn({
          event: 'snackbar_hide_outside_provider',
          message: 'hideSnackbar called outside SnackbarProvider',
        });
      },
    };
  }

  return ctx;
};
